/*
MIT LICENSE
Copyright (c) 2020-present Alibaba Cloud Design
Copied from antd/components/drawer/index.tsx
Copied from antd DrawerWrapper
Copyright (c) 2015-present Ant UED, https://xtech.antfin.com/

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE. */
import React from 'react';
import classNames from 'classnames';
import RcDrawer from 'rc-drawer';
import { ConfigContext } from '../config-provider';
import { tuple } from 'antd/lib/_util/type';
import useForceUpdate from 'antd/lib/_util/hooks/useForceUpdate';
import { Icon } from '../icon';
import { getMaskStyle } from '../_util/maskStyle';
const DrawerContext = React.createContext(null);
const PlacementTypes = tuple('top', 'right', 'bottom', 'left');
const defaultPushState = { distance: 480 };
//修改了不同尺寸的宽度和不同尺寸下的distance
const DrawerWrapper = React.forwardRef(({ width, height, size, closable, placement, maskClosable, mask, level, keyboard, push, closeIcon, bodyStyle, drawerStyle, prefixCls, className, direction, visible, children, zIndex, destroyOnClose, style, title, headerStyle, onClose, footer, footerStyle, extra, ...rest }, ref) => {
    const forceUpdate = useForceUpdate();
    const [internalPush, setPush] = React.useState(false);
    const parentDrawer = React.useContext(DrawerContext);
    const destroyClose = React.useRef(false);
    let defaultWidth = width;
    React.useEffect(() => {
        // fix: delete drawer in child and re-render, no push started.
        // <Drawer>{show && <Drawer />}</Drawer>
        if (visible && parentDrawer) {
            parentDrawer.push();
        }
        return () => {
            if (parentDrawer) {
                parentDrawer.pull();
                // parentDrawer = null;
            }
        };
    }, []);
    React.useEffect(() => {
        if (parentDrawer) {
            if (visible) {
                parentDrawer.push();
            }
            else {
                parentDrawer.pull();
            }
        }
    }, [visible]);
    const operations = React.useMemo(() => ({
        push() {
            if (push) {
                setPush(true);
            }
        },
        pull() {
            if (push) {
                setPush(false);
            }
        }
    }), [push]);
    React.useImperativeHandle(ref, () => operations, [operations]);
    const isDestroyOnClose = destroyOnClose && !visible;
    const onDestroyTransitionEnd = () => {
        if (!isDestroyOnClose) {
            return;
        }
        if (!visible) {
            destroyClose.current = true;
            forceUpdate();
        }
    };
    const getOffsetStyle = () => {
        if (!visible && !mask) {
            return {};
        }
        const offsetStyle = {};
        if (placement === 'left' || placement === 'right') {
            // 不同尺寸下的宽度
            if (size === 'small') {
                defaultWidth = 480;
            }
            else if (size === 'middle') {
                defaultWidth = 640;
            }
            else if (size === 'large') {
                defaultWidth = 800;
            }
            else if (size === 'fullscreen') {
                defaultWidth = '90%';
            }
            offsetStyle.width = typeof width === 'undefined' ? defaultWidth : width;
        }
        else {
            if (size === 'fullscreen') {
                offsetStyle.height = '90%';
            }
            else {
                offsetStyle.height = typeof height === 'undefined' ? 'auto' : height;
            }
        }
        return offsetStyle;
    };
    const getRcDrawerStyle = () => {
        // get drawer push width or height
        const getPushTransform = (_placement) => {
            let distance;
            //当全屏时推动距离为0
            if (size === 'fullscreen') {
                defaultWidth = 0;
            }
            if (typeof push === 'boolean') {
                distance = push ? defaultWidth : 0;
            }
            else {
                distance = push.distance;
            }
            distance = parseFloat(String(distance || 0));
            if (_placement === 'left' || _placement === 'right') {
                return `translateX(${_placement === 'left' ? distance : -distance}px)`;
            }
            if (_placement === 'top' || _placement === 'bottom') {
                return `translateY(${_placement === 'top' ? distance : -distance}px)`;
            }
        };
        // 当无 mask 时，将 width 应用到外层容器上
        // 解决 https://github.com/ant-design/ant-design/issues/12401 的问题
        const offsetStyle = mask ? {} : getOffsetStyle();
        return {
            zIndex,
            transform: internalPush ? getPushTransform(placement) : undefined,
            ...offsetStyle,
            ...style
        };
    };
    const closeIconNode = closable && (React.createElement("button", { type: 'button', onClick: onClose, "aria-label": 'Close', className: `${prefixCls}-close` }, closeIcon));
    function renderHeader() {
        if (!title && !closable) {
            return null;
        }
        return (React.createElement("div", { className: classNames(`${prefixCls}-header`, {
                [`${prefixCls}-header-close-only`]: closable && !title && !extra
            }), style: headerStyle },
            React.createElement("div", { className: `${prefixCls}-header-title` },
                closeIconNode,
                title && React.createElement("div", { className: `${prefixCls}-title` }, title))));
    }
    function renderFooter() {
        if (!footer && !extra) {
            return null;
        }
        return (React.createElement("div", { className: classNames(`${prefixCls}-footer`, {
                [`${prefixCls}-footer-justify`]: footer && extra,
                [`${prefixCls}-footer-end`]: !footer && extra
            }), style: footerStyle },
            footer && React.createElement("div", { className: `${prefixCls}-footer-left` }, footer),
            extra && React.createElement("div", { className: `${prefixCls}-extra` }, extra)));
    }
    // render drawer body dom
    const renderBody = () => {
        if (destroyClose.current && !visible) {
            return null;
        }
        destroyClose.current = false;
        const containerStyle = {};
        if (isDestroyOnClose) {
            // Increase the opacity transition, delete children after closing.
            containerStyle.opacity = 0;
            containerStyle.transition = 'opacity .3s';
        }
        return (React.createElement("div", { className: `${prefixCls}-wrapper-body`, style: {
                ...containerStyle,
                ...drawerStyle
            }, onTransitionEnd: onDestroyTransitionEnd },
            renderHeader(),
            React.createElement("div", { className: `${prefixCls}-body`, style: bodyStyle }, children),
            renderFooter()));
    };
    const drawerClassName = classNames({
        'no-mask': !mask,
        [`${prefixCls}-rtl`]: direction === 'rtl',
        [`${prefixCls}-${size}`]: size
    }, className);
    const offsetStyle = mask ? getOffsetStyle() : {};
    return (React.createElement(DrawerContext.Provider, { value: operations },
        React.createElement(RcDrawer, { handler: false, ...{
                placement,
                prefixCls,
                maskClosable,
                level,
                keyboard,
                children,
                onClose,
                ...rest
            }, ...offsetStyle, open: visible, showMask: mask, style: getRcDrawerStyle(), className: drawerClassName }, renderBody())));
});
DrawerWrapper.displayName = 'DrawerWrapper';
const Drawer = React.forwardRef((props, ref) => {
    const { prefixCls: customizePrefixCls, getContainer: customizeGetContainer } = props;
    const { getPopupContainer, animation, getPrefixCls, direction } = React.useContext(ConfigContext);
    const prefixCls = getPrefixCls('drawer', customizePrefixCls);
    const getContainer = 
    // 有可能为 false，所以不能直接判断
    customizeGetContainer === undefined && getPopupContainer
        ? () => getPopupContainer(document.body)
        : customizeGetContainer;
    return (React.createElement(DrawerWrapper, { maskStyle: getMaskStyle(animation), ...props, ref: ref, prefixCls: prefixCls, getContainer: getContainer, direction: direction }));
});
Drawer.displayName = 'Drawer';
Drawer.defaultProps = {
    size: 'small',
    closable: true,
    placement: 'right',
    maskClosable: true,
    mask: true,
    level: null,
    keyboard: true,
    push: defaultPushState,
    closeIcon: React.createElement(Icon, { type: 'close', size: 'large' })
};
// const ConfiguredDrawer = withConfigProvider(Drawer);
// export { ConfiguredDrawer as Drawer };
// export default ConfiguredDrawer;
export default Drawer;
